import { Component, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from 'src/app/services/auth.service';
import { versions } from 'src/app/versions';
import { passwordStrengthValidator } from '../password-rules';

type Field = 'first_name' | 'last_name' | 'email' | 'password' | 'invitation_code';

@Component({
  selector: 'app-user-register',
  templateUrl: './user-register.component.html',
  styleUrls: ['./user-register.component.scss'],
  standalone: false,
})
export class UserRegisterComponent implements OnInit {
  // Signals: the app does not repaint after async work on its own, so state set in the
  // HTTP callbacks (spinner, server errors) would otherwise never reach the screen.
  readonly isLoading = signal(false);
  readonly formError = signal<string | null>(null);
  readonly showPassword = signal(false);
  readonly versions = versions;

  form = new FormGroup({
    first_name: new FormControl('', Validators.required),
    last_name: new FormControl('', Validators.required),
    invitation_code: new FormControl('', Validators.required),
    email: new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl('', [Validators.required, Validators.minLength(5), passwordStrengthValidator]),
  });

  constructor(
    private authService: AuthService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    if (this.authService.getAccessToken() && !this.authService.isTokenExpired()) {
      this.router.navigate(['/main']);
    }
  }

  invalid(name: Field): boolean {
    const control = this.form.get(name);
    return !!control && control.touched && control.invalid;
  }

  has(name: Field, error: string): boolean {
    const control = this.form.get(name);
    return !!control && control.touched && control.hasError(error);
  }

  serverError(name: Field): string | null {
    return this.form.get(name)?.getError('serverError') ?? null;
  }

  register(): void {
    this.form.markAllAsTouched();
    this.formError.set(null);
    if (this.form.invalid || this.isLoading()) return;

    this.isLoading.set(true);
    this.authService.registerUser(this.form.value).subscribe({
      next: (res: any) => {
        this.isLoading.set(false);
        if (res) {
          this.router.navigate(['/main']);
        }
      },
      error: (err: any) => {
        this.isLoading.set(false);
        // The error interceptor may hand over the body itself or the full response.
        const errors = err?.error ?? err;
        let fieldError = false;
        for (const name of ['invitation_code', 'email', 'password'] as Field[]) {
          const message = errors?.[name];
          if (message) {
            this.form.get(name)?.setErrors({ serverError: Array.isArray(message) ? message[0] : String(message) });
            fieldError = true;
          }
        }
        if (!fieldError) {
          this.formError.set(errors?.detail ?? 'Something went wrong. Please try again.');
        }
      },
    });
  }
}
